import { makeAutoObservable, observable, toJS } from "mobx";
import type { RgBuilding } from "./RgCorePackage";
import type { BuildingChildren, BuildingData } from "@project-rouge/rg-core";
import { DefaultBuilding, RgCreate } from "@project-rouge/rg-core";
import type { Plot } from "./Plot";
import { ArchitecturalMetrics } from "./ArchitecturalMetrics";
import type { World } from "./World";
import { Dataset } from "src/constants/Dataset";

interface BuildingConfig {
  rgBuilding?: RgBuilding;
}

export class Building {
  children: BuildingChildren[] = [];
  private rgData: BuildingData | null = null;
  id: string;
  parent: Plot | undefined;
  constructor(config?: BuildingConfig) {
    this.id = config?.rgBuilding?.uuid ?? crypto.randomUUID();
    this.children = config?.rgBuilding?.children ?? [];
    this.rgData = config?.rgBuilding?.data ?? null;
    makeAutoObservable<Building, "rgData">(this, {
      children: observable.ref,
      rgData: observable.ref,
    });
  }

  get levelIndex() {
    return this.rgData?.index ?? -1;
  }

  get label() {
    let index = this.rgData?.index ?? -1;
    if (index < 0 || index === Infinity || index === -Infinity) {
      throw new Error("Invalid index");
    }

    let name = "";
    do {
      const charCode = 65 + (index % 26);
      name = String.fromCharCode(charCode) + name;
      index = Math.floor(index / 26) - 1;
    } while (index >= 0);

    return `Block ${name}`;
  }

  get world(): World | undefined {
    return this.parent?.parent?.parent?.parent;
  }
  get metrics(): ArchitecturalMetrics {
    return new ArchitecturalMetrics(this);
  }
  get floorCount(): number {
    return this.rgData?.levels.length ?? 0;
  }

  get rgBuilding(): RgBuilding {
    // @todo - use dataset from world or let user choose
    const dataset = Dataset;
    const rgBuilding = RgCreate<RgBuilding>(DefaultBuilding, dataset.rvData);
    rgBuilding.uuid = this.id;
    rgBuilding.children = this.children;
    rgBuilding.data = this.rgData ? toJS(this.rgData) : rgBuilding.data;
    for (const child of rgBuilding.children) {
      child.parent = rgBuilding;
    }
    return rgBuilding;
  }
  destroy(): void {
    this.parent?.removeBuilding(this);
  }
  setId(id: string) {
    this.id = id;
  }
}
